package modysy.view

import modysy.datastructures.Vector2
import modysy.menu.AbstractMenu
import modysy.model.GameObject
import modysy.tools.{Dialog, EnergyStorage, Toolbox}
import modysy.Clock
import modysy.simulation.events.coin

import org.scalajs.dom
import org.scalajs.dom.ext.KeyCode
import org.scalajs.dom.html.Canvas

import scala.collection.mutable
import modysy.menu.TextButton

/** Provides a canvas and a method to draw game objects onto that canvas */
class Render(widthLevel: Int, heightLevel: Int) {

  type Ctx2D = dom.CanvasRenderingContext2D

  /** The html canvas element */
  val canvas: Canvas =
    dom.document.createElement("canvas").asInstanceOf[Canvas]

  private val ctx: Ctx2D = canvas.getContext("2d").asInstanceOf[Ctx2D]
  private val MENU_BAR_HEIGHT: Int = 70
  private val CANVAS_ASPECT
    : Double = 1920.0 / (1080.0 - MENU_BAR_HEIGHT.toDouble) // TODO respect default level aspect

  {
    val screenWidth = dom.window.innerWidth.toInt
    val screenHeight = dom.window.innerHeight.toInt

    canvas.width = screenWidth
    canvas.height = (screenWidth.toDouble / CANVAS_ASPECT).toInt + MENU_BAR_HEIGHT

    if (canvas.height > screenHeight) {
      canvas.height = screenHeight
      canvas.width =
        ((screenHeight - MENU_BAR_HEIGHT).toDouble * CANVAS_ASPECT).toInt
    }

    dom.document.body.appendChild(canvas)
    canvas.focus()
  }

  // current canvas scrolling / world position of the upper left canvas corner
  private var screenPos: Vector2 = Vector2(0, 0)

  // current canvas scaling (1=100%, 0.5=50%, 2=200%, etc.)
  private var screenZoom: Double = this.minZoom

  // previous touch position while dragging to scroll
  private var touchDragPreviousCanvasPos: Option[Vector2] = None

  // requested camera scroll change as a result of gestures
  private var scrollInput: Vector2 = Vector2(0, 0)

  // previous distance while zooming
  private var touchZoomPreviousDistance: Option[Double] = None

  // requested camera zoom change as a result of gestures
  private var zoomInput: Double = 0.0

  /** Returns the current canvas width respecting any window resizing
    *
    * @return current canvas width
    */
  def width: Double = {
    canvas.width
  }

  /** Returns the current canvas height respecting any window resizing
    *
    * @return current canvas height
    */
  def height: Double = {
    canvas.height
  }

  private def minZoom: Double = {
    canvas.width.toDouble / 1920.0 // TODO magic numbers
  }

  def onWindowResize(): Unit = {
    val screenWidth = dom.window.innerWidth.toInt
    val screenHeight = dom.window.innerHeight.toInt

    canvas.width = screenWidth
    canvas.height = (screenWidth.toDouble / CANVAS_ASPECT).toInt + MENU_BAR_HEIGHT

    if (canvas.height > screenHeight) {
      canvas.height = screenHeight
      canvas.width =
        ((screenHeight - MENU_BAR_HEIGHT).toDouble * CANVAS_ASPECT).toInt
    }

    this.screenZoom = this.minZoom
  }

  def render(gameObjects: TraversableOnce[GameObject]): Unit = {
    ctx.fillStyle = Color.rgb(20, 20, 20)
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.save()
    ctx.translate(
      -screenPos.x * this.screenZoom,
      -screenPos.y * this.screenZoom + MENU_BAR_HEIGHT
    )
    ctx.scale(this.screenZoom, this.screenZoom)

    for (go <- gameObjects) {
      go.render(ctx)
    }

    ctx.restore()
  }

  def renderMenu(menu: AbstractMenu): Unit = {
    menu.render(ctx)
  }

  def renderNothing(): Unit = {
    ctx.fillStyle = Color.Black
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  def renderToolbox(toolbox: Toolbox, mousePosScreen: Option[Vector2]): Unit = {
    toolbox.renderStatics(ctx, mousePosScreen)

    ctx.save()
    ctx.translate(
      -screenPos.x * this.screenZoom,
      -screenPos.y * this.screenZoom
    )
    ctx.scale(this.screenZoom, this.screenZoom)

    toolbox.render(ctx, mousePosScreen)

    ctx.restore()
  }

  /** Returns the current canvas position relative to the upper left corner
    * of the canvas of the given screen position relative to the upper
    * left corner of the website.
    *
    * @param screenPosition screen position relative to the upper left website corner
    * @return canvas position relative to the upper left canvas corner
    */
  def screenToCanvas(screenPosition: Vector2): Vector2 = {
    Vector2(screenPosition.x - canvas.offsetLeft,
            screenPosition.y - canvas.offsetTop)
  }

  /** Returns the current screen position relative to the upper left corner
    * of the website of the given canvas position relative to the upper
    * left corner of the canvas.
    *
    * @param canvasPosition canvas position relative to the upper left canvas corner
    * @return screen position relative to the upper left website corner
    */
  def canvasToScreen(canvasPosition: Vector2): Vector2 = {
    Vector2(canvasPosition.x + canvas.offsetLeft,
            canvasPosition.y + canvas.offsetTop)
  }

  def canvasToWorldMenu(canvasPosition: Vector2): Vector2 = {
    Vector2(
      (canvasPosition.x / screenZoom) + screenPos.x,
      ((canvasPosition.y - MENU_BAR_HEIGHT) / screenZoom) + screenPos.y
    )
  }

  /** Returns the current world position relative to the upper left corner
    * of the game world of the given canvas position relative to the upper
    * left corner of the canvas with respect to the current zoom level and
    * any scrolling.
    *
    * @param canvasPosition canvas position relative to the upper left canvas corner
    * @return world position relative to the upper left world corner
    */
  def canvasToWorld(canvasPosition: Vector2): Vector2 = {
    Vector2(
      (canvasPosition.x / screenZoom) + screenPos.x,
      (canvasPosition.y / screenZoom) + screenPos.y
    )
  }

  /** Returns the current world position relative to the upper left corner
    * of the game world of the given screen position relative to the upper
    * left corner of the website with respect to the current zoom level and
    * any scrolling.
    *
    * @param screenPosition screen position relative to the upper left website corner
    * @return world position relative to the upper left world corner
    */
  def screenToWorld(screenPosition: Vector2): Vector2 = {
    this.canvasToWorld(this.screenToCanvas(screenPosition))
  }

  /** Returns the current screen position relative to the upper left corner
    * of the canvas of the given world position relative to the upper
    * left corner of the game world with respect to the current zoom level and
    * any scrolling.
    *
    * @param worldPosition world position relative to the upper left world corner
    * @return screen position relative to the upper left canvas corner
    */
  def worldToScreen(worldPosition: Vector2): Vector2 = {
    // TODO untested, probably broken, but currently unused ...
    var screenX = screenPos.x
    var screenY = screenPos.y

    if (this.width < (this.width / screenZoom) + screenX)
      screenX = this.width - (this.width / screenZoom)
    if (this.height < (this.height / screenZoom) + screenY) {
      screenY = this.height - (this.height / screenZoom)
    }

    Vector2(
      (worldPosition.x - screenX) * screenZoom,
      (worldPosition.y - screenY) * screenZoom
    )
  }

  /** Call this when the user starts to touch and touches the canvas at two or less spots simultaneously.
    *
    * A touch can be the beginning of a gesture moving or zooming the camera.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * Always returns false.
    *
    * @param touchCanvasPos canvas position of the starting touch relative to the upper left corner of the canvas
    * @return true, if the touch event can be considered as consumed, is always false
    */
  def onTouchStart(touchCanvasPos: Vector2): Boolean = {
    if (touchDragPreviousCanvasPos.isEmpty) { // first touch
      touchDragPreviousCanvasPos = Some(touchCanvasPos)
    } else { // second touch
      touchZoomPreviousDistance = Some(
        (touchCanvasPos - touchDragPreviousCanvasPos.get).norm
      )
      touchDragPreviousCanvasPos = None
    }
    false
  }

  /** Call this when the user moves his touch and touches the canvas at only one spot simultaneously.
    *
    * A single touch move can be the part of a gesture moving the camera.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touchCanvasPos canvas position of the moving touch relative to the upper left corner of the canvas
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchSingleMove(touchCanvasPos: Vector2): Boolean = {
    if (touchDragPreviousCanvasPos.isDefined) {
      scrollInput += touchDragPreviousCanvasPos.get - touchCanvasPos
      touchDragPreviousCanvasPos = Some(touchCanvasPos)

      return true
    }
    false
  }

  /** Call this when the user moves his touch and touches to the canvas at exactly two spots simultaneously.
    *
    * If camera zoom was enabled via the gesture, then moving the finger closer together or further away
    * will change the zoom of the camera.
    *
    * Returns true if the touch gesture has changed the camera zoom and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * Always returns true.
    *
    * @param touch1CanvasPos canvas position of the first touch relative to the upper left corner of the canvas
    * @param touch2CanvasPos canvas position of the second touch relative to the upper left corner of the canvas
    * @return true, if the touch event can be considered as consumed, is always true
    */
  def onTouchDoubleMove(
      touch1CanvasPos: Vector2,
      touch2CanvasPos: Vector2
  ): Boolean = {
    require(
      touchZoomPreviousDistance.isDefined,
      "called onTouchDoubleMove but touchZoomPreviousDistance empty"
    )

    val currentDistance = (touch1CanvasPos - touch2CanvasPos).norm
    zoomInput += currentDistance - touchZoomPreviousDistance.get
    touchZoomPreviousDistance = Some(currentDistance)

    true
  }

  /** Call this when the user ends all or all but one of his touches to the canvas.
    *
    * A ended touch can be the end of a gesture moving or zooming the camera.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * Always returns false.
    *
    * @return true, if the touch event can be considered as consumed, is always false
    */
  def onTouchEnd(): Boolean = {
    touchDragPreviousCanvasPos = None
    touchZoomPreviousDistance = None

    false
  }

  /** Calculates everything including 42, the zoom and the scroll required by the current
    * canvas size and the given input types.
    *
    * Some icons are drawn when the user has a mouse over the canvas and zooms or scrolls.
    *
    * Positive zoomInput values lead to closer zoom, negative values to less zoom.
    *
    * @param keys list containing the currently down keys, as a HashMap for no reason
    * @param mouseCanvasPos optional position of the mouse relative to the upper left corner of the canvas
    * @param zoomInput amount of zoom requested by user inputs
    */
  def updateZoomScroll(
      keys: mutable.HashMap[Int, Boolean],
      mouseCanvasPos: Option[Vector2],
      zoomInput: Double
  ): Unit = {

    val screenWidth: Double = canvas.width.toDouble
    val screenHeight: Double = canvas.height.toDouble

    val keyZoomSpeed = 0.025
    val scrollZoomSpeed = 0.025

    var zoom = (zoomInput + this.zoomInput) * scrollZoomSpeed
    this.zoomInput = 0.0

    if (keys.contains(KeyCode.Z)) {
      zoom = keyZoomSpeed
    } else if (keys.contains(KeyCode.T)) {
      zoom = -keyZoomSpeed
    }

    if (zoom > 0) { // zooming in
      val newPos: Vector2 = Vector2( // TODO ?¿what is happening here?¿
        screenPos.x + ((screenWidth / screenZoom) / 2 - (screenWidth / (screenZoom + zoom)) / 2),
        screenPos.y + ((screenHeight / screenZoom) / 2 - (screenHeight / (screenZoom + zoom)) / 2)
      )
      if (this.screenZoom + zoom < 5) {
        if (inScreen(newPos, screenZoom + zoom)) {
          this.screenPos = newPos
        }
        this.screenZoom = this.screenZoom + zoom
        if (mouseCanvasPos.isDefined)
          zoomIndicator(success = true, bigger = true, mouseCanvasPos.get)
      } else {
        this.screenZoom = 5
        if (mouseCanvasPos.isDefined)
          zoomIndicator(success = false, bigger = true, mouseCanvasPos.get)
      }
    } else if (zoom < 0) { // zooming out
      val newPos: Vector2 = Vector2( // TODO ?¿what is happening here?¿
        screenPos.x - ((screenWidth / (screenZoom + zoom)) / 2 - (screenWidth / screenZoom) / 2),
        screenPos.y - ((screenHeight / (screenZoom + zoom)) / 2 - (screenHeight / screenZoom) / 2)
      )
      if (this.screenZoom + zoom > this.minZoom) {
        this.screenZoom = this.screenZoom + zoom
        if (mouseCanvasPos.isDefined)
          zoomIndicator(success = true, bigger = false, mouseCanvasPos.get)
        if (inScreen(newPos, screenZoom + zoom)) {
          this.screenPos = newPos
        }
        if (this.screenPos.x >= 1920 - (this.width / screenZoom)) { // TODO magic numbers
          this.screenPos = Vector2(1920 - (this.width / screenZoom),
                                   this.screenPos.y) // TODO magic numbers
        }
        if (this.screenPos.y >= 1080 - (this.height - MENU_BAR_HEIGHT) / screenZoom - MENU_BAR_HEIGHT) { // TODO magic numbers
          this.screenPos = Vector2(
            this.screenPos.x,
            1080 - (this.height - MENU_BAR_HEIGHT) / screenZoom - MENU_BAR_HEIGHT) // TODO magic numbers
        }
      } else { // minimal zoom reached
        this.screenZoom = this.minZoom
        if (mouseCanvasPos.isDefined)
          zoomIndicator(success = false, bigger = false, mouseCanvasPos.get)
        this.screenPos = Vector2(0, 0)
      }
    }

    var moveX = scrollInput.x.toInt
    var moveY = scrollInput.y.toInt
    scrollInput = Vector2(0, 0)

    val keyMoveSpeed = 7
    val mouseMoveSpeed = 6

    val fastFactor = 2

    val TOOLBOX_SIZE = 10 // TO-DO should be loaded from Toolbox Class

    val mouseTrigger = screenHeight / 8
    val mouseTriggerFast = screenHeight / 32

    var fast = false

    if (mouseCanvasPos.isDefined) {
      if (mouseCanvasPos.get.x > screenWidth - mouseTrigger && mouseCanvasPos.get.x < screenWidth) {
        moveX = mouseMoveSpeed
        if (mouseCanvasPos.get.x > screenWidth - mouseTriggerFast) {
          moveX *= fastFactor
          fast = true
        }
      }
    }

    if (keys.contains(KeyCode.Right))
      moveX = keyMoveSpeed

    if (mouseCanvasPos.isDefined) {
      if (mouseCanvasPos.get.x < mouseTrigger && mouseCanvasPos.get.x > 0 && mouseCanvasPos.get.y > TOOLBOX_SIZE * 7) {
        moveX = -mouseMoveSpeed
        if (mouseCanvasPos.get.x < mouseTriggerFast) {
          moveX *= fastFactor
          fast = true
        }
      }
    }

    if (keys.contains(KeyCode.Left))
      moveX = -keyMoveSpeed

    if (mouseCanvasPos.isDefined) {
      if (mouseCanvasPos.get.y > 7 * mouseTrigger && mouseCanvasPos.get.y < screenHeight) {
        moveY = mouseMoveSpeed
        if (mouseCanvasPos.get.y > screenHeight - mouseTriggerFast) {
          moveY *= fastFactor
          fast = true
        }
      }
    }

    if (keys.contains(KeyCode.Down))
      moveY = keyMoveSpeed

    if (mouseCanvasPos.isDefined) {
      if (mouseCanvasPos.get.y < (TOOLBOX_SIZE * 7) + mouseTrigger && mouseCanvasPos.get.y > TOOLBOX_SIZE * 7) {
        moveY = -mouseMoveSpeed
        if (mouseCanvasPos.get.y < (TOOLBOX_SIZE * 7) + mouseTriggerFast) {
          moveY *= fastFactor
          fast = true
        }
      }
    }

    if (keys.contains(KeyCode.Up))
      moveY = -keyMoveSpeed

    var successX = false
    var successY = false

    if (moveX != 0) {
      if (this.screenPos.x + moveX < 0) {
        this.screenPos = Vector2(0, this.screenPos.y)
      } else if (this.screenPos.x + moveX >= 1920 - this.width / screenZoom) { // TODO magic numbers
        this.screenPos = Vector2(1920 - this.width / screenZoom,
                                 this.screenPos.y) // TODO magic numbers
      } else {
        this.screenPos = Vector2(this.screenPos.x + moveX, this.screenPos.y)
        successX = true
      }
    }
    if (moveY != 0) {
      if (this.screenPos.y + moveY < 0) {
        this.screenPos = Vector2(this.screenPos.x, 0)
      } else if (this.screenPos.y + moveY >= 1080 - (this.height - MENU_BAR_HEIGHT) / screenZoom - MENU_BAR_HEIGHT) { // TODO magic numbers
        this.screenPos = Vector2(
          this.screenPos.x,
          1080 - (this.height - MENU_BAR_HEIGHT) / screenZoom - MENU_BAR_HEIGHT // TODO magic numbers
        )
      } else {
        this.screenPos = Vector2(this.screenPos.x, this.screenPos.y + moveY)
        successY = true
      }
    }

    if (mouseCanvasPos.isDefined)
      this.moveIndicator(successX,
                         successY,
                         fast,
                         moveX,
                         moveY,
                         mouseCanvasPos.get)
  }

  /** Gets called by camera Function and renders a +/- sign at the mouse position when zooming key is pressed
    *
    * @param success true -> zooming successful / false -> limit reached
    * @param bigger true -> zoom in / false -> zoom out
    * @param mouse mouse position
    */
  private def zoomIndicator(success: Boolean,
                            bigger: Boolean,
                            mouse: Vector2): Unit = {
    var text = ""

    if (success)
      ctx.fillStyle = Color.White
    else
      ctx.fillStyle = Color.Grey

    if (bigger)
      text = "+"
    else
      text = "-"

    ctx.font = "70px Arial black"
    ctx.fillText(text, mouse.x - 20, mouse.y + 20)
  }

  /** Checks whether a screenPosition would be still visible with a given level of zoom
    *
    * @param screenPosition screenPosition you want to check
    * @param zoom level of zoom you want to check
    */
  private def inScreen(screenPosition: Vector2, zoom: Double): Boolean = {
    if (screenPosition.x <= 1920 - this.width / zoom) // TODO magic numbers
      if (screenPosition.y <= 1080 - (this.height - MENU_BAR_HEIGHT) / zoom - MENU_BAR_HEIGHT) // TODO magic numbers
        if (screenPosition.x >= 0)
          if (screenPosition.y >= MENU_BAR_HEIGHT)
            return true

    false
  }

  /** Gets called by camera Function and renders a arrow sign at the mouse position when mouse is moved
    *
    * @param successX true -> move in X Direction successful
    * @param successY true -> move in Y Direction successful
    * @param moveX movement in X Direction
    * @param moveY movement in Y Direction
    * @param mouse mouse position
    */
  private def moveIndicator(
      successX: Boolean,
      successY: Boolean,
      fast: Boolean,
      moveX: Int,
      moveY: Int,
      mouse: Vector2
  ): Unit = {
    if (moveX != 0 || moveY != 0) {

      var text = ""

      if (successX || successY) {
        if (fast)
          ctx.fillStyle = Color.Red_Light
        else
          ctx.fillStyle = Color.White
      } else
        ctx.fillStyle = Color.Grey

      if (moveX > 0 && moveY == 0)
        text = "🢂"
      if (moveX < 0 && moveY == 0)
        text = "🢀"
      if (moveX == 0 && moveY > 0)
        text = "🢃"
      if (moveX == 0 && moveY < 0)
        text = "\uD83E\uDC81"
      if (moveX < 0 && moveY > 0)
        text = "\uD83E\uDC87"
      if (moveX < 0 && moveY < 0)
        text = "\uD83E\uDC84"
      if (moveX > 0 && moveY > 0)
        text = "\uD83E\uDC86"
      if (moveX > 0 && moveY < 0)
        text = "\uD83E\uDC85"

      ctx.font = "50px Arial"
      ctx.fillText(text, mouse.x - 25, mouse.y + 25)
    }
  }

  def renderClock(clock: Clock): Unit = {
    ctx.save()
    ctx.translate(this.width - 425, 60) // use this to position the clock rendering
    clock.render(ctx)
    ctx.restore()
  }

  /** Renders the current balance and up to the last four movements of the
    * given EnergyStorage in the lower left corner of the canvas.
    *
    * @param energyStorage the EnergyStorage to be rendered
    */
  def renderEnergyStorage(energyStorage: EnergyStorage): Unit = {
    ctx.save()
    ctx.translate(25, this.height - 25) // use this to position the energyStorage rendering
    energyStorage.render(ctx)
    ctx.restore()
  }

  def renderDialog(dialog: Dialog): Unit = {
    ctx.save()
    dialog.render(ctx)
    ctx.restore()
  }

  def renderCoin(coinItem: coin): Unit = {
    coinItem.render(ctx)
  }

  def renderPausebutton(p: TextButton) = {
    p.render(ctx)
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/view/Render.scala